import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { useForkRef } from '@mui/material/utils';
import { observer } from 'mobx-react';
import { useWikiHelpTooltip } from './useWikiHelpTooltip';
import { canUseFastWindow, notifyTooltipWillOpen, recordFastWindowHeartbeat, registerTooltipOpenListener } from './wikiHelpTooltipManager';

const DEFAULT_HOVER_DELAY_MS = 1500;
const DEFAULT_FAST_WINDOW_MS = 2000;
const DEFAULT_CLOSE_DELAY_MS = 300;
const TOOLTIP_TRANSITION_TIMEOUT = { appear: 150, enter: 150, exit: 120 };

type SyntheticHandler<E extends React.SyntheticEvent> = ((event: E) => void) | undefined;

function composeEventHandlers<E extends React.SyntheticEvent>(originalHandler: SyntheticHandler<E>, ourHandler?: (event: E) => void) {
  if (!originalHandler && !ourHandler) {
    return undefined;
  }
  return (event: E) => {
    if (typeof originalHandler === 'function') {
      originalHandler(event);
    }
    if (event.defaultPrevented) {
      return;
    }
    if (typeof ourHandler === 'function') {
      ourHandler(event);
    }
  };
}

export type WikiHelpTooltipProps = {
  tooltipId: string;
  tooltipPlacement?: TooltipProps['placement'];
  children: React.ReactElement;
  hoverDelayMs?: number;
  fastOpenWindowMs?: number;
  closeDelayMs?: number;
  errorFallback?: React.ReactNode | ((error: unknown) => React.ReactNode);
};

function WikiHelpTooltip({
  tooltipId,
  tooltipPlacement,
  children,
  hoverDelayMs = DEFAULT_HOVER_DELAY_MS,
  fastOpenWindowMs = DEFAULT_FAST_WINDOW_MS,
  closeDelayMs = DEFAULT_CLOSE_DELAY_MS,
  errorFallback,
}: WikiHelpTooltipProps) {
  const { status, entryState, tooltipContent, tooltipSlotProps, triggerRef, resolvedPlacement } = useWikiHelpTooltip({
    tooltipId,
    tooltipPlacement,
  });

  const [open, setOpen] = useState(false);
  const tooltipInstanceIdRef = useRef<symbol>(Symbol('wikiHelpTooltip'));
  const hoverDelayTimerRef = useRef<number | null>(null);
  const closeDelayTimerRef = useRef<number | null>(null);
  const isTriggerHoveringRef = useRef(false);
  const isTooltipHoveringRef = useRef(false);
  const pendingOpenRef = useRef(false);

  const clearHoverDelayTimer = useCallback(() => {
    if (hoverDelayTimerRef.current !== null) {
      window.clearTimeout(hoverDelayTimerRef.current);
      hoverDelayTimerRef.current = null;
    }
  }, []);

  const clearCloseDelayTimer = useCallback(() => {
    if (closeDelayTimerRef.current !== null) {
      window.clearTimeout(closeDelayTimerRef.current);
      closeDelayTimerRef.current = null;
    }
  }, []);

  const cancelTimers = useCallback(() => {
    clearHoverDelayTimer();
    clearCloseDelayTimer();
  }, [clearHoverDelayTimer, clearCloseDelayTimer]);

  const closeTooltipImmediately = useCallback(() => {
    let didClose = false;
    setOpen((prev) => {
      if (!prev) {
        return prev;
      }
      didClose = true;
      return false;
    });
    if (didClose) {
      recordFastWindowHeartbeat(fastOpenWindowMs);
    }
  }, [fastOpenWindowMs]);

  const scheduleCloseIfNeeded = useCallback(() => {
    if (isTriggerHoveringRef.current || isTooltipHoveringRef.current) {
      return;
    }

    if (!closeDelayMs) {
      closeTooltipImmediately();
      return;
    }

    clearCloseDelayTimer();
    closeDelayTimerRef.current = window.setTimeout(() => {
      closeDelayTimerRef.current = null;
      closeTooltipImmediately();
    }, closeDelayMs);
  }, [clearCloseDelayTimer, closeDelayMs, closeTooltipImmediately]);

  const requestOpen = useCallback(() => {
    if (!isTriggerHoveringRef.current) {
      return;
    }
    if (!tooltipContent) {
      pendingOpenRef.current = true;
      return;
    }
    pendingOpenRef.current = false;
    notifyTooltipWillOpen(tooltipInstanceIdRef.current);
    setOpen(true);
    recordFastWindowHeartbeat(fastOpenWindowMs);
  }, [fastOpenWindowMs, tooltipContent, tooltipInstanceIdRef]);

  const handlePointerEnter = useCallback(() => {
    isTriggerHoveringRef.current = true;
    pendingOpenRef.current = false;
    clearHoverDelayTimer();
    clearCloseDelayTimer();

    if (canUseFastWindow(fastOpenWindowMs)) {
      requestOpen();
      return;
    }

    hoverDelayTimerRef.current = window.setTimeout(() => {
      hoverDelayTimerRef.current = null;
      requestOpen();
    }, hoverDelayMs);
  }, [clearCloseDelayTimer, clearHoverDelayTimer, fastOpenWindowMs, hoverDelayMs, requestOpen]);

  const handlePointerLeave = useCallback(() => {
    isTriggerHoveringRef.current = false;
    pendingOpenRef.current = false;
    clearHoverDelayTimer();
    scheduleCloseIfNeeded();
  }, [clearHoverDelayTimer, scheduleCloseIfNeeded]);

  const handleTooltipEnter = useCallback(() => {
    isTooltipHoveringRef.current = true;
    clearCloseDelayTimer();
  }, [clearCloseDelayTimer]);

  const handleTooltipLeave = useCallback(() => {
    isTooltipHoveringRef.current = false;
    scheduleCloseIfNeeded();
  }, [scheduleCloseIfNeeded]);

  useEffect(() => {
    return () => {
      cancelTimers();
    };
  }, [cancelTimers]);

  useEffect(() => {
    const unsubscribe = registerTooltipOpenListener(tooltipInstanceIdRef.current, () => {
      pendingOpenRef.current = false;
      cancelTimers();
      closeTooltipImmediately();
    });
    return unsubscribe;
  }, [cancelTimers, closeTooltipImmediately]);

  useEffect(() => {
    if (!tooltipContent) {
      pendingOpenRef.current = false;
      setOpen(false);
      return;
    }
    if (!pendingOpenRef.current || !isTriggerHoveringRef.current) {
      return;
    }
    pendingOpenRef.current = false;
    notifyTooltipWillOpen(tooltipInstanceIdRef.current);
    setOpen(true);
    recordFastWindowHeartbeat(fastOpenWindowMs);
  }, [fastOpenWindowMs, tooltipContent, tooltipInstanceIdRef]);

  const child = React.Children.only(children);
  const childProps = child.props as React.DOMAttributes<HTMLElement>;

  const mergedRef = useForkRef((child as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref, triggerRef);

  const interactiveChild = React.cloneElement(child, {
    ref: mergedRef,
    onMouseEnter: composeEventHandlers(childProps.onMouseEnter, handlePointerEnter),
    onMouseLeave: composeEventHandlers(childProps.onMouseLeave, handlePointerLeave),
    onFocus: composeEventHandlers(childProps.onFocus, handlePointerEnter),
    onBlur: composeEventHandlers(childProps.onBlur, handlePointerLeave),
  });

  const contentReady = Boolean(tooltipContent);

  const tooltipSlotPropsWithHover = useMemo<TooltipProps['slotProps']>(() => {
    return {
      ...tooltipSlotProps,
      tooltip: {
        ...(tooltipSlotProps?.tooltip ?? {}),
        onMouseEnter: composeEventHandlers(tooltipSlotProps?.tooltip?.onMouseEnter, handleTooltipEnter),
        onMouseLeave: composeEventHandlers(tooltipSlotProps?.tooltip?.onMouseLeave, handleTooltipLeave),
      },
    };
  }, [handleTooltipEnter, handleTooltipLeave, tooltipSlotProps]);

  if (status === 'error' && !entryState?.data) {
    if (typeof errorFallback === 'function') {
      return <>{errorFallback(entryState?.error)}</>;
    }
    if (errorFallback) {
      return <>{errorFallback}</>;
    }
    return children;
  }

  if (!contentReady) {
    return interactiveChild;
  }

  return (
    <Tooltip
      title={tooltipContent}
      placement={resolvedPlacement}
      arrow
      disableInteractive={false}
      slotProps={tooltipSlotPropsWithHover}
      open={open}
      disableFocusListener
      disableTouchListener
      disableHoverListener
      TransitionProps={{ timeout: TOOLTIP_TRANSITION_TIMEOUT }}
    >
      {interactiveChild}
    </Tooltip>
  );
}

export default observer(WikiHelpTooltip);
